import {useEffect} from 'react';
import {useSearchParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {actionCreators, type State, store} from '@App/state'
import {bindActionCreators} from "@reduxjs/toolkit";
import {useGarageParameters} from "@App/hooks/url/useGarageParameters";
import {useVehicletreeParameters} from "@App/hooks/url/useVehicletreeParameters";
import {useServiceParameters} from "@App/hooks/url/useServiceParameters";
import {usePlaceParameters} from "@App/hooks/url/usePlaceParameters";
import {useGarageChainParameters} from "@App/hooks/url/useGarageChainParameters";
import {useOrganizationParameters} from "@App/hooks/url/useOrganizationParameters";
import {usePremiumServiceParameters} from "@App/hooks/url/usePremiumServiceParameters";
import {useEngineTypeParameters} from "@App/hooks/url/useEngineTypeParameters";
import {useCustomerParameters} from "@App/hooks/url/useCustomerParameters";
import {useJwtTokenParameters} from "@App/hooks/url/useJwtTokenParameters";

/**
 * A custom hook that checks if all needed data was loaded from the api on component mount
 * and returns a method to parse the data to the url query parameters.
 */
export const useQueryParams = () => {
    const {garageQueryParams} = useGarageParameters()
    const {vehicletreeQueryParams} = useVehicletreeParameters()
    const {serviceQueryParams} = useServiceParameters()
    const {placeQueryParams} = usePlaceParameters()
    const {garageChainQueryParams} = useGarageChainParameters()
    const {organizationQueryParams} = useOrganizationParameters()
    const {premiumServiceQueryParams} = usePremiumServiceParameters()
    const {engineTypeQueryParams} = useEngineTypeParameters()
    const {customerQueryParams} = useCustomerParameters()
    const {jwtTokenParams} = useJwtTokenParameters()

    const dispatch = useDispatch();
    const {
        changeNameFilter,
        addSubRepair
    } = bindActionCreators(actionCreators, dispatch)

    const [searchParams] = useSearchParams()

    const showAsNonBookable = useSelector((state: State) => state.filters.showAsNonBookable)

    const search = useSelector((state: State) => state.filters.search)

    useEffect(() => {
        const searchQuery = searchParams.get('search')
        const subRepairsQuery = searchParams.get('subRepairs')

        // set search query if not in state
        if (searchQuery && searchQuery !== 'null' && (!search || searchQuery !== search)) {
            changeNameFilter(searchQuery)
        }

        // set search query if not in state
        if (subRepairsQuery && subRepairsQuery !== 'null' && (!search || subRepairsQuery !== search)) {
            subRepairsQuery.split(',').forEach(subRepair => addSubRepair(subRepair))
        }
    }, [])

    // if search changes, automatically update the query parameters
    useEffect(() => {
        if (search && search !== searchParams.get('search')) {
            searchParams.set('search', search)
        }
        // navigate(location.pathname + createQuery(), {replace: true})
    }, [search])

    useEffect(() => {
        if (showAsNonBookable) {
            searchParams.set('showAsNonBookable', showAsNonBookable.toString())
        }
    }, [showAsNonBookable])

    const createQuery = (query_params?: Record<string, string>) => {
        return `?${new URLSearchParams({
            ...(organizationQueryParams()),
            ...(serviceQueryParams()),
            ...(vehicletreeQueryParams()),
            ...(garageQueryParams()),
            ...(placeQueryParams()),
            ...(garageChainQueryParams()),
            ...(premiumServiceQueryParams()),
            ...(engineTypeQueryParams()),
            ...(customerQueryParams()),
            // add search if existing
            ...(store.getState().filters.search && {
                search: store.getState().filters.search
            }),
            ...(store.getState().filters.showAsNonBookable && {
                showAsNonBookable: store.getState().filters.showAsNonBookable.toString()
            }),
            // add subRepairs if existing
            ...(store.getState().orders.subRepairs && store.getState().orders.subRepairs.length > 0 && {
                subRepairs: store.getState().orders.subRepairs.join(',')
            }),
            ...(jwtTokenParams()),
            ...query_params,
        })}`
    }

    return {createQuery}
}

